<template>
  <div>
    <div
      v-if="shouldShowFab"
      class="grainfox-fab-container"
    >
      <v-card
        elevation="8"
        class="pa-2 rounded-pill"
        :style=" { border: '5px solid ' + COLORS.grainfoxAIButtonBorder }"
        @click="openAssistantDialog"
      >
        <CyclicPlaceholder
          v-slot="{ text }"
          v-bind="contextText ? { overrideText: contextText } : {}"
        >
          <v-text-field
            v-model="userInput"
            solo
            hide-details
            flat
            dense
            readonly
            class="pointer-events-none"
            :class="{ 'highlight-fab-text': $isMobile && highlightText }"
            :style="$isMobile && highlightText ? { '--fab-label-color': COLORS.grainfoxAIButton } : {}"
            :label="text"
            append-icon="mdi-arrow-right-circle"
            v-bind="textFieldProps"
            @click="openAssistantDialog"
            @focus="openAssistantDialog"
          >
            <template #append>
              <v-icon
                :color="COLORS.grainfoxAIButton"
                class="arrow-icon"
                :size="35"
                :style="{ transform: 'translateX(10px)'}"
              >
                mdi-arrow-right-circle
              </v-icon>
            </template>
            <template #prepend-inner>
              <img
                src="/static/dashboard/gif/grainfox-ai.gif"
                alt="sinoa-logo"
                class="mr-2 grainfox-logo"
                :style="{ transform: 'translateX(-10px)' }"
              >
            </template>
          </v-text-field>
        </CyclicPlaceholder>
      </v-card>
    </div>
    <GrainFoxAssistantDialog />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import GrainFoxAssistantDialog from './GrainFoxAssistantDialog.vue';
import { COLORS } from '../../helpers/colors';
import CyclicPlaceholder from './CyclicPlaceholder.vue';
import { datadogRum } from '@datadog/browser-rum';

export default {
  name: 'FloatingAssistantBtn',

  components: {
    GrainFoxAssistantDialog,
    CyclicPlaceholder,
  },

  props: {
    contextText: {
      type: String,
      default: undefined,
    },

    delegateClick: {
      type: Boolean,
      default: false,
    },

    highlightText: {
      type: Boolean,
      default: false,
    },
  },

  async mounted() {
    // Only initialize Sinoa if not already initialized to prevent
    // duplicate API calls during component re-renders (e.g., scrolling in panel mode)
    if (!this.isSinoaInitialized) {
      await this.initializeSinoa();
    }
  },

  watch: {
    isGrainFoxAssistantDialogOpen(newVal) {
      if (newVal) {
        datadogRum.addAction('Sinoa: Open Chat', {
          source: 'FloatingAssistantBtn',
          entry: 'FAB',
          isMobile: this.$isMobile ?? null,
          isMimic: this.isMimic,
        });
      }
    },
  },

  data() {
    return {
      userInput: null,
      COLORS,
    };
  },

  computed: {
    ...mapState('chatbot', ['isGrainFoxAssistantDialogOpen', 'isSinoaInitialized']),
    ...mapState('app', ['isMimic']),

    textFieldProps() {
      return (this.$isMobile && this.highlightText)
        ? { color: this.COLORS.grainfoxAIButton }
        : {};
    },

    /**
     * Check if user came from Sinoa scanner - hide FAB when true
     * @returns {boolean}
     */
    isFromSinoa() {
      return this.$route?.query?.is_from_sinoa === 'true';
    },

    /**
     * Whether to show the floating button
     * Hide when dialog is open OR when coming from Sinoa (panel mode active)
     * @returns {boolean}
     */
    shouldShowFab() {
      return !this.isGrainFoxAssistantDialogOpen && !this.isFromSinoa;
    },
  },

  methods: {
    ...mapActions('chatbot', ['initializeSinoa']),
    ...mapMutations('chatbot', ['setGrainFoxAssistantDialogOpen']),

    openAssistantDialog() {
      this.$emit('click');
      if (this.delegateClick) {
        return;
      }
      this.setGrainFoxAssistantDialogOpen(true);
    },
  },
};
</script>

<style scoped>
.grainfox-fab-container {
  position: fixed;
  left: 50%;
  bottom: 5%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 400px;
  z-index: 120;
}

.grainfox-logo {
  height: 40px;
  max-height: 5vh;
  width: auto;
}

@media (max-width: 600px) {
  .grainfox-logo {
    height: 32px;
  }

}

::v-deep .highlight-fab-text .v-label {
  color: var(--fab-label-color) !important;
}
</style>
